// src/components/ShareProductDialog.jsx
//
// Модальное окно для шаринга товара.
// - Показывает превью картинки 1080×1080 (отрисованной в canvas)
// - Текстовое поле с caption (редактируемое)
// - Чекбокс "Сохранить как шаблон" (запоминается в localStorage)
// - Кнопки "Поделиться" и "В Stories"

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import {
  shareProductCard,
  shareProductToStories,
  getCaptionTemplate,
  saveCaptionTemplate,
  fillCaption,
  pickProductPhotoUrl,
  DEFAULT_CAPTION_TEMPLATE,
} from '../utils/shareProduct';
import { generateProductCardSquare, generateProductCardVertical } from '../utils/productCardImage';
import { formatMoney } from '../data/currencies';

export default function ShareProductDialog({ product, shop, currency, onClose, onError, onSuccess }) {
  const { t } = useTranslation();
  const [caption, setCaption] = useState('');
  const [template, setTemplate] = useState(getCaptionTemplate());
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [previewSrcSquare, setPreviewSrcSquare] = useState(null);
  const [previewSrcVertical, setPreviewSrcVertical] = useState(null);
  const [previewFormat, setPreviewFormat] = useState('square'); // 'square' | 'vertical'
  const [previewLoading, setPreviewLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [tab, setTab] = useState('text'); // 'text' | 'preview'
  const initRef = useRef(false);

  // Подготавливаем caption при открытии (и пересоздаём если template изменился)
  useEffect(() => {
    const filled = fillCaption(template, {
      name: product?.name || '',
      price: formatMoney(product?.salePrice || 0, currency),
      description: product?.description || '',
      shop: shop?.name || '',
      shopId: shop?.id || '',
    });
    setCaption(filled);
    // eslint-disable-next-line
  }, [product, shop, currency]);

  // Генерируем превью обоих форматов
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      setPreviewLoading(true);
      const params = {
        photoUrl: pickProductPhotoUrl(product),
        name: product?.name,
        price: formatMoney(product?.salePrice || 0, currency),
        shopName: shop?.name,
      };
      try {
        const sq = await generateProductCardSquare(params);
        setPreviewSrcSquare(`data:image/png;base64,${sq}`);
      } catch (e) {}
      try {
        const vt = await generateProductCardVertical(params);
        setPreviewSrcVertical(`data:image/png;base64,${vt}`);
      } catch (e) {}
      setPreviewLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  const handleShare = async (mode) => {
    if (sharing) return;
    setSharing(true);

    // Если пользователь хочет сохранить новый шаблон — но текст уже содержит
    // подставленные значения! Чтобы сохранить как шаблон, надо обратно подставить
    // переменные. Самое простое решение — мы НЕ автоматически делаем "reverse template",
    // а просто сохраняем то что пользователь написал. Если хочет — сам поставит {name} итд.
    if (saveAsTemplate) {
      saveCaptionTemplate(caption);
    }

    try {
      const fn = mode === 'stories' ? shareProductToStories : shareProductCard;
      const result = await fn({
        product, shop, caption, currency, formatMoney,
      });
      if (result.cancelled) {
        setSharing(false);
        return;
      }
      onSuccess?.(result);
      onClose();
    } catch (e) {
      console.error('share error:', e);
      onError?.(e);
    }
    setSharing(false);
  };

  const resetCaption = () => {
    const filled = fillCaption(DEFAULT_CAPTION_TEMPLATE, {
      name: product?.name || '',
      price: formatMoney(product?.salePrice || 0, currency),
      description: product?.description || '',
      shop: shop?.name || '',
      shopId: shop?.id || '',
    });
    setCaption(filled);
    setTemplate(DEFAULT_CAPTION_TEMPLATE);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 520,
          maxHeight: '92vh',
          background: 'var(--bg)',
          borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
          padding: '16px 16px calc(env(safe-area-inset-bottom) + 16px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Хэндл и заголовок */}
        <div style={{
          width: 36, height: 4,
          background: 'var(--border)',
          borderRadius: 2,
          margin: '0 auto 12px',
        }} />

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ flex: 1, fontSize: 18, fontWeight: 800 }}>
            {t('shareProduct.title')}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', padding: 8, cursor: 'pointer', color: 'var(--fg-muted)' }}
          >
            <Icon name="x" size={20} />
          </button>
        </div>

        {/* Табы */}
        <div style={{
          display: 'flex',
          background: 'var(--bg-subtle)',
          padding: 4,
          borderRadius: 'var(--r-sm)',
          marginBottom: 12,
        }}>
          <TabBtn active={tab === 'text'} onClick={() => setTab('text')}>
            {t('shareProduct.tabText')}
          </TabBtn>
          <TabBtn active={tab === 'preview'} onClick={() => setTab('preview')}>
            {t('shareProduct.tabPreview')}
          </TabBtn>
        </div>

        {/* Контент таба */}
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {tab === 'text' ? (
            <>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('shareProduct.captionLabel')}
              </label>
              <textarea
                className="input"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={9}
                style={{
                  width: '100%',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  lineHeight: 1.5,
                  resize: 'vertical',
                  minHeight: 180,
                  padding: 12,
                }}
              />

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: 'var(--fg-muted)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={saveAsTemplate}
                  onChange={(e) => setSaveAsTemplate(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                {t('shareProduct.saveAsTemplate')}
              </label>

              <button
                onClick={resetCaption}
                style={{
                  background: 'none', border: 'none',
                  color: 'var(--brand-primary)', fontSize: 13,
                  padding: '6px 0', cursor: 'pointer',
                  fontFamily: 'var(--font-display)', fontWeight: 600,
                  marginTop: 4,
                }}
              >
                {t('shareProduct.resetCaption')}
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              {/* Переключатель формата */}
              <div style={{
                display: 'inline-flex',
                background: 'var(--bg-subtle)',
                padding: 3,
                borderRadius: 'var(--r-xs)',
                marginBottom: 14,
              }}>
                <FormatBtn
                  active={previewFormat === 'square'}
                  onClick={() => setPreviewFormat('square')}
                >
                  {t('shareProduct.formatSquare')}
                </FormatBtn>
                <FormatBtn
                  active={previewFormat === 'vertical'}
                  onClick={() => setPreviewFormat('vertical')}
                >
                  {t('shareProduct.formatVertical')}
                </FormatBtn>
              </div>

              {previewLoading ? (
                <div style={{ padding: 60 }}>
                  <div className="spinner" style={{ margin: '0 auto 14px' }} />
                  <p style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
                    {t('shareProduct.previewLoading')}
                  </p>
                </div>
              ) : (() => {
                const src = previewFormat === 'vertical' ? previewSrcVertical : previewSrcSquare;
                if (!src) {
                  return (
                    <p style={{ color: 'var(--fg-muted)', padding: 40, fontSize: 14 }}>
                      {t('shareProduct.previewError')}
                    </p>
                  );
                }
                const isVertical = previewFormat === 'vertical';
                return (
                  <img
                    src={src}
                    alt="preview"
                    style={{
                      width: 'auto',
                      maxWidth: isVertical ? 240 : 360,
                      maxHeight: '50vh',
                      aspectRatio: isVertical ? '9 / 16' : '1 / 1',
                      objectFit: 'contain',
                      borderRadius: 'var(--r-md)',
                      boxShadow: 'var(--shadow-md)',
                    }}
                  />
                );
              })()}
            </div>
          )}
        </div>

        {/* Кнопки */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button
            className="btn btn-secondary"
            onClick={() => handleShare('stories')}
            disabled={sharing}
            style={{ flex: 1, fontSize: 14 }}
          >
            📷 {t('shareProduct.toInstagram')}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => handleShare('share')}
            disabled={sharing}
            style={{ flex: 1, fontSize: 14 }}
          >
            <Icon name="share" size={16} stroke={2.2} />
            {t('shareProduct.share')}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '8px',
        background: active ? 'var(--bg-elevated)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--r-xs)',
        fontWeight: 700,
        fontSize: 13,
        color: active ? 'var(--fg)' : 'var(--fg-muted)',
        cursor: 'pointer',
        boxShadow: active ? 'var(--shadow-xs)' : 'none',
        fontFamily: 'var(--font-display)',
      }}
    >
      {children}
    </button>
  );
}

function FormatBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px',
        background: active ? 'var(--bg-elevated)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--r-xs)',
        fontWeight: 700,
        fontSize: 12,
        color: active ? 'var(--fg)' : 'var(--fg-muted)',
        cursor: 'pointer',
        boxShadow: active ? 'var(--shadow-xs)' : 'none',
        fontFamily: 'var(--font-display)',
      }}
    >
      {children}
    </button>
  );
}
